import type { TypedDocumentNode } from '@apollo/client';
import { gql, useSubscription } from '@apollo/client';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Drawer } from '@sorare/core/src/atoms/layout/Drawer';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { HeadlineM } from '@sorare/core/src/atoms/typography';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { isType } from '@sorare/core/src/lib/gql';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { useAnySportEvents } from '@sorare/marketplace/src/shared/events/lib';

import { FootballBench } from './FootballBench';
import { FootballField } from './FootballField';
import { MatchPlayingStatusOdds } from './MatchPlayingStatusOdds';
import { PlayerScoreDetailsView } from './PlayerScoreDetailsView';
import type {
  MatchViewFormationsQuery,
  MatchViewFormationsQueryVariables,
  MatchView_game,
  MatchView_gameSubscription,
  onMatchViewDataUpdated,
  onMatchViewDataUpdatedVariables,
} from './__generated__/index.graphql';

const LoaderContainer = styled.div`
  color: var(--c-white);
  padding: var(--quadruple-unit) 0;
`;
const MatchViewContainer = styled(Vertical).attrs({ gap: 0 })`
  width: 100%;
  flex-grow: 1;
  border-radius: var(--double-unit);
  padding: 0 0 var(--container-padding);

  @media ${desktopAndAbove} {
    padding: var(--container-padding);
  }
`;
const Substitutes = styled(Vertical)`
  padding: 0 var(--container-padding) var(--container-padding);
`;
const SubstitutesTitleWrapper = styled(Horizontal)`
  padding: 0 var(--double-unit);
`;
const TeamLogo = styled.img`
  width: var(--quadruple-unit);
`;
const CenteredHeadlineM = styled(HeadlineM)`
  margin: auto;
`;
const Benchs = styled.div`
  display: grid;
  align-items: start;
  grid-template-columns: repeat(2, 1fr);

  & > *:first-child {
    border-right: 1px solid var(--c-nd-150);
  }
`;

const gameSubscriptionFragment = gql`
  fragment MatchView_gameSubscription on Game {
    id
    homeFormation {
      bench {
        slug
        id
        ...FootballBench_anyPlayerInterfaceSubscription
      }
    }
    awayFormation {
      bench {
        slug
        id
        ...FootballBench_anyPlayerInterfaceSubscription
      }
    }
    ...FootballField_gameSubscription
  }
  ${FootballField.fragments.subscriptions.game}
  ${FootballBench.fragments.subscriptions.anyPlayerInterface}
` as TypedDocumentNode<MatchView_gameSubscription>;

const gameFragment = gql`
  fragment MatchView_game on Game {
    id
    awayScore
    homeScore
    homeFormation {
      bench {
        slug
        id
        ...FootballBench_anyPlayer
      }
    }
    awayFormation {
      bench {
        slug
        id
        ...FootballBench_anyPlayer
      }
    }
    ...FootballField_game
  }
  ${FootballField.fragments.game}
  ${FootballBench.fragments.anyPlayer}
` as TypedDocumentNode<MatchView_game>;

const subscription = gql`
  subscription onMatchViewDataUpdated($id: ID!) {
    aGameWasUpdated(id: $id) {
      id
      ...MatchView_gameSubscription
    }
  }
  ${gameSubscriptionFragment}
` as TypedDocumentNode<onMatchViewDataUpdated, onMatchViewDataUpdatedVariables>;

const MATCH_VIEW_FORMATIONS_QUERY = gql`
  query MatchViewFormationsQuery($id: ID!) {
    football {
      game(id: $id) {
        id
        status: statusTyped
        homeFormation {
          startingLineupAvailable
        }
        awayFormation {
          startingLineupAvailable
        }
        playerGameScores(includingLegends: false) {
          id
          anyPlayerGameStats {
            id
            ... on PlayerGameStats {
              id
              footballPlayingStatusOdds {
                starterOddsBasisPoints
              }
            }
          }
        }
        ...MatchView_game
        ...MatchPlayingStatusOdds_game
      }
    }
  }
  ${gameFragment}
  ${MatchPlayingStatusOdds.fragments.game}
` as TypedDocumentNode<
  MatchViewFormationsQuery,
  MatchViewFormationsQueryVariables
>;

type Props = {
  id: string;
  desktop?: boolean;
  onPlayerSelect?: (playerSlug: string, scoreId?: string) => void;
  highlightedPlayerSlugs?: string[];
  highlightInLineupPlayers?: boolean;
};
export const MatchView = ({
  id,
  desktop,
  onPlayerSelect,
  highlightedPlayerSlugs,
  highlightInLineupPlayers,
}: Props) => {
  useSubscription(subscription, { variables: { id: idFromObject(id) } });
  const track = useAnySportEvents();
  const [selectedPlayerSlug, setSelectedPlayerSlug] = useState<
    string | undefined
  >(undefined);
  const [isPlayerDetailsOpen, setIsPlayerDetailsOpen] =
    useState<boolean>(false);

  const gameId = idFromObject(id);
  const { data: formationData, loading: formationLoading } = useQuery(
    MATCH_VIEW_FORMATIONS_QUERY,
    {
      variables: {
        id: gameId!,
      },
      skip: !gameId,
    }
  );

  useEffect(() => {
    if (formationData) {
      track('Open Match View', {
        gameId,
        gameStatus: formationData.football.game.status,
        deviceType: `web_${desktop ? 'desktop' : 'mobile'}`,
        interactionContext: 'match_view',
      });
    }
  }, [desktop, formationData, gameId, track]);

  if (formationLoading || !formationData) {
    return (
      <LoaderContainer>
        <LoadingIndicator small />
      </LoaderContainer>
    );
  }
  const { game } = formationData.football;

  const onPlayerDetailsClick = (playerSlug?: string, scoreId?: string) => {
    if (onPlayerSelect && playerSlug) {
      onPlayerSelect(playerSlug, scoreId);
    } else if (isPlayerDetailsOpen && selectedPlayerSlug === playerSlug) {
      setIsPlayerDetailsOpen(false);
    } else {
      setSelectedPlayerSlug(playerSlug);
      setIsPlayerDetailsOpen(true);
      if (playerSlug) {
        track('Open Match View Player Details Drawer', {
          playerSlug,
        });
      }
    }
  };
  const onPlayerDetailsClose = () => setIsPlayerDetailsOpen(false);

  const hasStartingPrediction = game.playerGameScores.some(
    pgs =>
      isType(pgs.anyPlayerGameStats, 'PlayerGameStats') &&
      (pgs.anyPlayerGameStats?.footballPlayingStatusOdds
        ?.starterOddsBasisPoints || 0) > 0
  );

  const hasStartingLineup =
    game.homeFormation.startingLineupAvailable ||
    game.awayFormation.startingLineupAvailable;

  return (
    <>
      {!!selectedPlayerSlug && (
        <Drawer open={isPlayerDetailsOpen} side="right">
          <PlayerScoreDetailsView
            playerSlug={selectedPlayerSlug}
            gameId={id}
            onClose={onPlayerDetailsClose}
            side="right"
          />
        </Drawer>
      )}
      <Vertical gap={0}>
        {hasStartingPrediction && !hasStartingLineup ? (
          <MatchPlayingStatusOdds
            game={game}
            onPlayerDetailsClick={onPlayerDetailsClick}
          />
        ) : (
          <MatchViewContainer>
            <FootballField
              game={game}
              highlightedPlayerSlugs={highlightedPlayerSlugs}
              highlightInLineupPlayers={highlightInLineupPlayers}
              onPlayerDetailsClick={onPlayerDetailsClick}
              desktop={desktop}
            />
          </MatchViewContainer>
        )}

        {!!(
          game.homeFormation.bench.length || game.awayFormation.bench.length
        ) && (
          <Substitutes gap={3}>
            <SubstitutesTitleWrapper>
              {!!game.homeTeam?.pictureUrl && (
                <TeamLogo src={game.homeTeam.pictureUrl} alt="" />
              )}
              <CenteredHeadlineM as="h3">
                <FormattedMessage
                  id="MatchView.bench.title"
                  defaultMessage="Bench"
                />
              </CenteredHeadlineM>
              {!!game.awayTeam?.pictureUrl && (
                <TeamLogo src={game.awayTeam.pictureUrl} alt="" />
              )}
            </SubstitutesTitleWrapper>

            <Benchs>
              <FootballBench
                benchPlayers={game.homeFormation.bench}
                highlightedPlayerSlugs={highlightedPlayerSlugs}
                onClick={onPlayerDetailsClick}
                desktop={desktop}
              />
              <FootballBench
                benchPlayers={game.awayFormation.bench}
                highlightedPlayerSlugs={highlightedPlayerSlugs}
                onClick={onPlayerDetailsClick}
                desktop={desktop}
              />
            </Benchs>
          </Substitutes>
        )}
      </Vertical>
    </>
  );
};
